import React, { useEffect, useState, memo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  useTheme
} from '@mui/material';
import { motion, useSpring, useTransform } from 'framer-motion';
import {
  Assessment as AssessmentIcon,
  Timer as TimerIcon,
  EmojiEvents as TrophyIcon,
  Today as TodayIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';

// Icon mapping for different KPI types
const iconMap = {
  'total_open': AssessmentIcon,
  'avg_resolution': TimerIcon,
  'top_performer': TrophyIcon,
  'sa_opened_today': TodayIcon,
  'sa_closed_today': CheckCircleIcon,
  'data_status': RefreshIcon,
};

// Use topbar color for all KPI types
const getTopbarColor = (theme) => theme?.colors?.background?.topbar || '#0d3475';

/**
 * Animated Counter Component using framer-motion
 */
const AnimatedCounter = ({ value, formatValue }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const spring = useSpring(displayValue, { stiffness: 100, damping: 30 });

  useEffect(() => {
    const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    if (numericValue !== displayValue) {
      spring.set(numericValue);
    }
  }, [value, spring, displayValue]);

  useEffect(() => {
    const unsubscribe = spring.on("change", (current) => {
      const rounded = Math.round(current);
      const formatted = formatValue ? formatValue(rounded) : rounded.toLocaleString();
      setDisplayValue(formatted);
    });
    return unsubscribe;
  }, [spring, formatValue]);

  return <span>{displayValue}</span>;
};

/**
 * KPICard Component
 * Individual KPI card with Material-UI design and framer-motion animations
 */
const KPICard = ({
  title,
  value,
  previousValue = null,
  subtitle = '',
  kpiType = 'total_open',
  loading = false,
  formatValue = null,
  compact = false
}) => {
  const theme = useTheme();
  const IconComponent = iconMap[kpiType] || AssessmentIcon;
  const cardColor = getTopbarColor(theme);

  // Calculate change from previous value
  const getChangeIndicator = () => {
    if (!previousValue || previousValue === value) return null;
    
    const numericValue = typeof value === 'number' ? value : parseFloat(value) || 0;
    const numericPrevious = typeof previousValue === 'number' ? previousValue : parseFloat(previousValue) || 0;
    const change = numericValue - numericPrevious;
    const isPositive = change > 0;
    
    return {
      change,
      isPositive,
      color: isPositive ? theme.palette.success.main : theme.palette.error.main
    };
  };

  const changeIndicator = getChangeIndicator();

  if (loading) {
    const cardStyles = compact ? {
      width: '100%',
      height: '100%',
      minWidth: 120,
      minHeight: 44,
      borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
      border: `1px solid rgba(255, 255, 255, 0.2)`,
      backgroundColor: 'rgba(13, 52, 117, 0.85)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)', // Safari support
    } : {
      width: '100%',
      height: '100%',
      // minHeight: 120,
      borderRadius: 3,
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)',
      border: `1px solid rgba(255, 255, 255, 0.2)`,
      backgroundColor: 'rgba(13, 52, 117, 0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)', // Safari support
      // '&:hover': {
      //   boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.12)',
      //   transform: 'translateY(-2px)',
      //   backgroundColor: 'rgba(13, 52, 117, 0.9)',
      // },
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    };

    const contentStyles = compact ? {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      p: 1,
      '&:last-child': { pb: 1 }
    } : {
      textAlign: 'center',
      p: 3,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%'
    };

    return (
      <Card
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        sx={cardStyles}
      >
        <CardContent sx={contentStyles}>
          {compact ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                <Skeleton variant="circular" width={18} height={18} />
                <Skeleton variant="text" width={80} height={16} />
              </Box>
              <Skeleton variant="text" width={60} height={20} />
            </>
          ) : (
            <>
              <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto', mb: 2 }} />
              <Skeleton variant="text" width="80%" sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="60%" height={40} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="50%" sx={{ mx: 'auto' }} />
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  const cardStyles = compact ? {
    width: '100%',
    height: 44,
    minWidth: 120,
    minHeight: 44,
    borderRadius: 2,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 4px 16px rgba(0, 0, 0, 0.08)',
    border: `1px solid rgba(255, 255, 255, 0.2)`,
    backgroundColor: 'rgba(13, 52, 117, 0.85)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)', // Safari support
    // cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    // '&:hover': {
    //   boxShadow: '0 12px 40px rgba(0, 0, 0, 0.16), 0 6px 20px rgba(0, 0, 0, 0.1)',
    //   transform: 'translateY(-1px)',
    //   backgroundColor: 'rgba(13, 52, 117, 0.9)',
    //   border: `1px solid rgba(255, 255, 255, 0.3)`,
    // },
    // Responsive adjustments for overlay positioning
    '@media (max-width: 768px)': {
      minWidth: 100,
      height: 40,
      minHeight: 40,
    },
  } : {
    width: '100%',
    height: '100%',
    minHeight: 120,
    borderRadius: 3,
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 6px 20px rgba(0, 0, 0, 0.1)',
    border: `1px solid rgba(255, 255, 255, 0.2)`,
    backgroundColor: 'rgba(13, 52, 117, 0.85)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)', // Safari support
    // cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    // '&:hover': {
    //   boxShadow: '0 16px 48px rgba(0, 0, 0, 0.2), 0 8px 24px rgba(0, 0, 0, 0.12)',
    //   transform: 'translateY(-2px)',
    //   backgroundColor: 'rgba(13, 52, 117, 0.9)',
    //   border: `1px solid rgba(255, 255, 255, 0.3)`,
    // },
    // Responsive adjustments for overlay positioning
    '@media (max-width: 768px)': {
      minHeight: 100,
      borderRadius: 2,
    },
    '@media (max-width: 480px)': {
      minHeight: 80,
      borderRadius: 2,
    },
  };

  const contentStyles = compact ? {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height:'100%',
    p: 1,
    '&:last-child': { pb: 1 }
  } : {
    textAlign: 'center',
    p: 3,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%'
  };

  // const hoverProps = compact ? {} : {
  //   whileHover: {
  //     y: -4,
  //     boxShadow: theme.shadows[8]
  //   }
  // };

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      // {...hoverProps}
      transition={{ duration: 0.3 }}
      sx={cardStyles}
    >
      <CardContent sx={contentStyles}>
        {compact ? (
          <>
            {/* Compact Layout: Icon + Label + Value */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 , height:'100%'}}>
              <IconComponent
                sx={{
                  fontSize: 24,
                  color: 'white',
                  flexShrink: 0
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight:'1.1rem',
                  textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', // Enhanced text shadow for better readability
                  '@media (max-width: 768px)': {
                    fontSize: '0.9rem',
                  },
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: '1.1rem',
                textAlign: 'right',
                flexShrink: 0,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.3)', // Enhanced text shadow for better readability
                '@media (max-width: 768px)': {
                  fontSize: '0.9rem',
                },
              }}
            >
              {typeof value === 'number' ? (
                <AnimatedCounter
                  value={value}
                  formatValue={formatValue}
                />
              ) : (
                formatValue ? formatValue(value) : value
              )}
            </Typography>
          </>
        ) : (
          <>
            {/* Full Layout */}
            {/* Icon */}
            <Box sx={{ mb: 1 }}>
              <IconComponent
                sx={{
                  fontSize: 32,
                  color: 'white',
                  filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.3))', // Enhanced shadow for better visibility
                  '@media (max-width: 768px)': {
                    fontSize: 28,
                  },
                  '@media (max-width: 480px)': {
                    fontSize: 24,
                  },
                }}
              />
            </Box>

            {/* Title */}
            <Typography
              variant="body2"
              sx={{
                color: 'white',
                fontWeight: 600,
                mb: 1,
                textTransform: 'uppercase',
                letterSpacing: 0.3,
                fontSize: '0.8rem',
                lineHeight: 1.2,
                textShadow: '0 1px 3px rgba(0, 0, 0, 0.4)', // Enhanced text shadow for better readability
                '@media (max-width: 768px)': {
                  fontSize: '0.75rem',
                },
                '@media (max-width: 480px)': {
                  fontSize: '0.7rem',
                },
              }}
            >
              {title}
            </Typography>

            {/* Value with Animation */}
            <Typography
              variant="h4"
              component="div"
              sx={{
                color: 'white',
                fontWeight: 700,
                mb: subtitle ? 0.5 : 1,
                lineHeight: 1.1,
                fontSize: '1.8rem',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.4)', // Enhanced text shadow for better readability
                '@media (max-width: 768px)': {
                  fontSize: '1.6rem',
                },
                '@media (max-width: 480px)': {
                  fontSize: '1.4rem',
                },
              }}
            >
              {typeof value === 'number' ? (
                <AnimatedCounter
                  value={value}
                  formatValue={formatValue}
                />
              ) : (
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  {formatValue ? formatValue(value) : value}
                </motion.span>
              )}
            </Typography>

            {/* Subtitle */}
            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontSize: '0.75rem',
                  lineHeight: 1.2,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)', // Enhanced text shadow for better readability
                  '@media (max-width: 768px)': {
                    fontSize: '0.7rem',
                  },
                  '@media (max-width: 480px)': {
                    fontSize: '0.65rem',
                  },
                }}
              >
                {subtitle}
              </Typography>
            )}

            {/* Change Indicator */}
            {changeIndicator && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.3 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: changeIndicator.color,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    mt: 0.5,
                    display: 'block',
                    lineHeight: 1.2,
                    textShadow: '0 1px 2px rgba(0, 0, 0, 0.4)', // Enhanced text shadow for better readability
                    filter: 'brightness(1.1)', // Slightly brighten the change indicator for better visibility
                    '@media (max-width: 768px)': {
                      fontSize: '0.6rem',
                    },
                    '@media (max-width: 480px)': {
                      fontSize: '0.55rem',
                    },
                  }}
                >
                  {changeIndicator.isPositive ? '↗' : '↘'} {Math.abs(changeIndicator.change).toLocaleString()}
                </Typography>
              </motion.div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

KPICard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  previousValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtitle: PropTypes.string,
  kpiType: PropTypes.oneOf(['total_open', 'avg_resolution', 'top_performer', 'sa_opened_today', 'sa_closed_today', 'data_status']),
  loading: PropTypes.bool,
  formatValue: PropTypes.func,
  compact: PropTypes.bool
};


export default memo(KPICard);